import { DataAccess, ResponseMessage } from "../../../dataaccess/data.access";
import { S25Util } from "../../../util/s25-util";
import { TaskService } from "../../../services/task/task.service";
import { Task } from "../../../pojo/Task";
import { ContactService } from "../../../services/contact.service";
import { Workflow } from "./Workflow";
import { Invalidate } from "../../../decorators/cache.decorator";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25Dateformat } from "../../s25-dateformat/s25.dateformat.service";

export class WorkflowService {
    /**
     * Available system actions with their input definitions
     */
    public static readonly SYSTEM_ACTIONS = {
        SEND_EMAIL: {
            actionCode: "SEND_EMAIL",
            displayName: "Send Email",
            description: "Send an email notification",
            inputs: [
                { inputCode: "subject", label: "Subject", required: true, type: "text" },
                { inputCode: "body", label: "Body", required: true, type: "textarea" },
                { inputCode: "to", label: "To", required: true, type: "text", help: "Comma-separated email addresses" },
                { inputCode: "from", label: "From", required: false, type: "text", help: "Email address (optional)" },
            ],
        },
        CREATE_EVENT: {
            actionCode: "CREATE_EVENT",
            displayName: "Create Event",
            description: "Create a new event from form data",
        },
    } satisfies Record<string, Workflow.SystemAction>;

    /**
     * Get all workflow processes
     */
    public static async getWorkflowProcesses(enabled?: boolean): Promise<Workflow.WorkflowProcess[]> {
        const params = new URLSearchParams();
        if (S25Util.isDefined(enabled)) {
            params.set("enabled", enabled.toString());
        }
        const processes =
            (await DataAccess.get<Workflow.WorkflowProcess[]>(`/workflow/processes.json?${params.toString()}`)) ?? [];
        processes.sort(S25Util.shallowSort("workflowProcessName", true));
        return processes;
    }

    /**
     * Get workflow process by ID
     */
    public static async getWorkflowProcess(workflowProcessId: number): Promise<Workflow.WorkflowProcess> {
        return await DataAccess.get<Workflow.WorkflowProcess>(`/workflow/process/${workflowProcessId}.json`);
    }

    /**
     * Create a new workflow process
     */
    public static createWorkflowProcess(workflowProcess: Workflow.WorkflowProcess): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>("/workflow/process.json", workflowProcess);
    }

    /**
     * Update an existing workflow process
     */
    public static updateWorkflowProcess(
        workflowProcessId: number,
        workflowProcess: Workflow.WorkflowProcess,
    ): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(`/workflow/process/${workflowProcessId}.json`, workflowProcess);
    }

    /**
     * Delete a workflow process
     */
    public static deleteWorkflowProcess(workflowProcessId: number): Promise<ResponseMessage> {
        return DataAccess.delete<ResponseMessage>(`/workflow/process/${workflowProcessId}.json`);
    }

    /**
     * Get submissions assigned to current user
     */
    public static async getAssignedSubmissions(): Promise<Workflow.WorkflowItem[]> {
        const submissions = (await DataAccess.get<Workflow.WorkflowItem[]>("/workflow/assigned.json")) ?? [];
        return submissions
            .sort((a, b) => new Date(a.submissionDate).getTime() - new Date(b.submissionDate).getTime())
            .map((submission) => ({ ...submission, itemTypeName: this.getItemTypeName(submission.itemTypeId) }));
    }

    public static getItemTypeName(itemTypeId: number): string {
        switch (itemTypeId) {
            case Workflow.WORKFLOW_INGESTION_SUBMISSION_OBJECT_TYPE:
                return "Ingestion Submission";
            default:
                return "Unknown";
        }
    }

    /**
     * Get submissions assigned to current user with contact names populated
     */
    public static async getAssignedSubmissionsWithContactNames(): Promise<Workflow.WorkflowItem[]> {
        const submissions = await this.getAssignedSubmissions();

        if (submissions.length === 0) {
            return submissions;
        }

        // Get unique contact IDs
        const contactIds = [...new Set(submissions.map((s) => s.contId).filter(S25Util.isDefined))];

        if (contactIds.length === 0) {
            return submissions;
        }

        try {
            // Fetch all contact names in one call
            const contactNames = await ContactService.getContactsNameAndFormal(contactIds);
            const contactNameMap = new Map(contactNames.map((c) => [c.itemId, c.itemName]));

            // Annotate submissions with contact names
            return submissions.map((submission) => ({
                ...submission,
                submitterName: contactNameMap.get(submission.contId) || `Contact ${submission.contId}`,
            }));
        } catch (error) {
            console.error("Error fetching contact names:", error);
            // Return submissions without contact names if the call fails
            return submissions.map((submission) => ({
                ...submission,
                submitterName: `Contact ${submission.contId}`,
            }));
        }
    }

    /**
     * Get workflow history for a submission
     */
    public static getWorkflowHistory(itemTypeId: number, itemId: number): Promise<Task.DBTodo[]> {
        const params = new URLSearchParams();
        params.set("itemTypeId", itemTypeId.toString());
        params.set("itemId", itemId.toString());
        return DataAccess.get<Task.DBTodo[]>(`/workflow/history.json?${params.toString()}`);
    }

    /**
     * Get requested changes from todo comments for a workflow item
     */
    public static getRequestedChanges(itemTypeId: number, itemId: number): Promise<Task.DBTodo[]> {
        const params = new URLSearchParams();
        params.set("itemTypeId", itemTypeId.toString());
        params.set("itemId", itemId.toString());
        return DataAccess.get<Task.DBTodo[]>(`/workflow/requested-changes.json?${params.toString()}`);
    }

    /**
     * Request changes for a workflow submission using todo ID
     */
    public static requestChangesFromTodo(todoId: number, requestedChanges: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(`/workflow/todo/${todoId}/request-changes.json`, requestedChanges);
    }

    /**
     * Approve a workflow submission using todo ID
     */
    public static async approveSubmissionFromTodo(todoId: number): Promise<ResponseMessage> {
        try {
            const results = await TaskService.changeNonEventTask({ itemId: todoId.toString() }, Task.States.Completed);
            if (results?.messageCode) {
                return results;
            }
            if (results && results.length > 0 && "success" in results[0]) {
                return { message: "Submission approved successfully" };
            } else {
                return { message: "Failed to approve submission" };
            }
        } catch (error) {
            console.error("Error approving submission:", error);
            return { message: "Error approving submission: " + error };
        }
    }

    /**
     * Deny a workflow submission using todo ID
     */
    public static async denySubmissionFromTodo(todoId: number): Promise<ResponseMessage> {
        try {
            const results = await TaskService.changeNonEventTask({ itemId: todoId.toString() }, Task.States.Denied);
            if (results?.messageCode) {
                return results;
            }
            if (results && results.length > 0 && "success" in results[0]) {
                return { message: "Submission denied successfully" };
            } else {
                return { message: "Failed to deny submission" };
            }
        } catch (error) {
            console.error("Error denying submission:", error);
            return { message: "Error denying submission: " + error };
        }
    }

    /**
     * Initialize the workflow system
     */
    @Invalidate({ serviceName: "EventService", methodName: "getEventTypeById" })
    public static initialize(): Promise<ResponseMessage> {
        return DataAccess.post("/workflow/initialize.json");
    }

    /**
     * Check if the workflow system is initialized
     */
    public static isInitialized(): Promise<boolean> {
        return DataAccess.get("/workflow/isInitialized.json");
    }

    /**
     * Get workflow action history for a date range
     */
    public static async getActionHistory(startDate: Date, endDate: Date): Promise<Workflow.WorkflowActionHistory[]> {
        const formattedStartDate = S25Datefilter.transform(startDate, S25Dateformat.getISODateTimeFormat());
        const formattedEndDate = S25Datefilter.transform(endDate, S25Dateformat.getISODateTimeFormat());

        const encodedStartDate = encodeURIComponent(formattedStartDate);
        const encodedEndDate = encodeURIComponent(formattedEndDate);

        return DataAccess.get<Workflow.WorkflowActionHistory[]>(
            `/workflow/action-history.json?startDate=${encodedStartDate}&endDate=${encodedEndDate}`,
        );
    }
}
